<template>
  <div
    id="app"
    :style="appBGColor"
  >
    <transition
      name="ink"
      appear
    >
      <div
        id="ink"
        :key="currentNetwork.color"
        :style="{
          backgroundColor: currentNetwork && currentNetwork.type === 'color' ? currentNetwork.color : false,
          backgroundImage: currentNetwork && currentNetwork.type === 'gradient' ? currentNetwork.color : ''
        }"
        @transitionend="changeAppColor"
      />
    </transition>
    <div class="foreground">
      <div class="slit"/>
      <h2
        v-show="loading"
        :style="{color: currentNetwork.textColor === 'dark' ? 'rgba(0,0,0,0.5)' : ''}"
        class="loading-text">Preparing to print...</h2>
      <div
        :style="{height: `${(Math.min(windowWidth, 500) - 60) / 440 * currentNetwork.height + 60}px`}"
        class="receipt-container"
        @mousedown="swipeStart"
        @touchstart="swipeStart"
      >
        <transition
          appear
          name="slide-down"
        >
          <img
            v-show="showReceipt"
            :src="currentNetwork.receipt"
            :key="currentNetwork.receipt"
            alt="receipt"
            @dragstart.prevent
            @load="imgLoad"
          >
        </transition>
      </div>
      <footer class="main-footer">
        <h1
          :style="{color: currentNetwork.textColor === 'dark' ? 'rgba(0,0,0,0.5)' : ''}"
          class="title">The Price of<br >Social Media</h1>
        <span
          :style="{color: currentNetwork.textColor === 'dark' ? 'rgba(0,0,0,0.5)' : ''}"
          class="copyright-info">© 2018{{ new Date().getFullYear().toString() !== '2018' ? `– ${new Date().getFullYear()}` : '' }}, Amadeus Maximilian Stadler</span>
        <nav
          :style="{color: currentNetwork.textColor === 'dark' ? 'black' : ''}"
          class="navigation"
        >
          <ul>
            <li @click="animateToTop">Top</li>
            <li @click="about = true">About</li>
            <li @click="disclaimer = true">Disclaimer</li>
          </ul>
        </nav>
      </footer>
    </div>
    <price-modal
      :visible="about"
      @close="about = false"
    >
      <article>
        <h1>About</h1>
        <p>Arguably, data is one of the most precious resources of the 21st century. It can be used for anything, from scientific discoveries to predicting behaviour. Due to its value, it has long become a form of payment.</p>
        <p>However, many don’t seem to quite acknowledge this fact, often giving away their data carelessly without even realising what they’re giving away. This might be in part their own fault, and in part due to the often convoluted and confusing data collection and privacy policies published by companies.</p>
        <p>To remedy this fact, I decided to illustrate (some) of the data collected by various companies—in this case social networks, but the same principle could apply to any other sort of digital service—in the form of receipts. After all, when you buy something for money, you also get a receipt as proof of your payment, listing exactly how much you paid for what.</p>
        <p>It is important to note, however, that many of the pieces of data collected by service providers are necessary for them to provide their services. After all, nobody can see a tweet if it doesn’t get stored and published in some form by Twitter, and neither can anybody like a picture on Instagram if that action can’t be recorded. Elements like payment details, address data, etc. are required for very clear purposes. Some other pieces of information, however, might only serve to create profiles and beahviour patterns.</p>
        <p>In the end, everybody should decide for themselves whether they want to pay with the data a service is requiring, but I believe everybody has the right to know exactly what data will be collected of them.</p>
        <h2>Background</h2>
        <p>I created this project for my typography class in my second semester at the <a href="https://hm.edu">University of Applied Sciences Munich</a>. The overarching topic for the project was “Typography in my…”, interpreted by me as “Typography in my wallet” choosing to focus not on money, but receipts from various stores.</p>
      </article>
    </price-modal>
    <price-modal
      :visible="disclaimer"
      @close="disclaimer = false"
    >
      <article>
        <h1>Disclaimer</h1>
        <h2>Privacy Policy</h2>
        <p>This site does not collect any user data. However, it is hosted on <a
          href="https://netlify.com"
          target="_blank">Netlify</a> in order to be publicly accessible. Netlify stores the IP addresses of site visitors for less than 30 days. For more information, please turn to their <a
            href="https://www.netlify.com/privacy"
            target="_blank">Privacy Policy</a> and <a
              href="https://www.netlify.com/gdpr"
              target="_blank">GDPR Compliance Statement</a>.</p>
        <h2>Please note</h2>
        <p>This is an artistic project, none of the here shown information is to be considered absolutely correct. What you can read on the various receipts has been extracted and summarised from the privacy policies of the companies listed below. However, many of these companies use clauses like “such as” and therefore the information listed here might very well be incomplete.</p>
        <p>This project is in no way affiliated and / or endorsed by the various companies listed. Their logos are only used to illustrate their brands and are owned by their respective copyright holders.</p>
        <h2>Sources</h2>
        <ul>
          <li><a
            href="https://dribbble.com/privacy"
            target="_blank">Dribbble</a></li>
          <li><a
            href="https://www.facebook.com/privacy/explanation"
            target="_blank">Facebook</a></li>
          <li><a
            href="https://help.instagram.com/519522125107875"
            target="_blank">Instagram</a></li>
          <li><a
            href="https://medium.com/policy/medium-privacy-policy-f03bf92035c9"
            target="_blank">Medium</a></li>
          <li><a
            href="https://policy.pinterest.com/de/privacy-policy"
            target="_blank">Pinterest</a></li>
          <li><a
            href="https://www.snap.com/de-DE/privacy/privacy-policy/"
            target="_blank">Snapchat</a></li>
          <li><a
            href="https://www.tumblr.com/privacy/en_eu"
            target="_blank">Tumblr</a></li>
          <li><a
            href="https://twitter.com/en/privacy"
            target="_blank">Twitter</a></li>
          <li><a
            href="https://policies.google.com/privacy"
            target="_blank">YouTube</a></li>
        </ul>
      </article>
    </price-modal>
    <interaction-hint
      :visible="showInteractionHint"
      :background-color="currentNetwork.color"
      :background-image="currentNetwork.type === 'color' ? '' : currentNetwork.color"
      :text-color="currentNetwork.textColor"
    />
  </div>
</template>

<script>
import anime from 'animejs';
import SocialNetworks from './SocialNetworks';
import InteractionHint from './components/InteractionHint.vue';
import PriceModal from './components/PriceModal.vue';

export default {
  name: 'App',
  components: {
    InteractionHint,
    PriceModal,
  },
  data() {
    return {
      about: false,
      appBGColor: {},
      disclaimer: false,
      currentNetwork: null,
      currentNetworkIndex: null,
      inkTimeout: null,
      interactionHintShown: false,
      loading: true,
      networks: false,
      showInteractionHint: false,
      shownNetworks: [],
      showReceipt: false,
      swipeData: {
        maxDistance: null,
        start: null,
        ticking: false,
      },
      swipeAnimation: null,
    };
  },
  computed: {
    socialNetworks() {
      return SocialNetworks;
    },
    windowWidth() {
      return window.innerWidth;
    },
  },
  watch: {
    currentNetwork() {
      // needed so the animation can correctly be reattached to the element, which gets swapped out by vue (I think)
      this.swipeAnimation = anime({
        targets: '.receipt-container',
        rotate: '-30deg',
        translateX: {
          value: '100%',
          delay: 200,
        },
        translateY: {
          value: '25%',
          delay: 200,
        },
        opacity: {
          value: 0,
          delay: 200,
        },
        duration: 1000,
        easing: 'easeOutCubic',
        autoplay: false,
      });
    },
  },
  created() {
    this.selectNetwork();
  },
  methods: {
    animateToTop() {
      const distance = document.body.scrollTop || document.documentElement.scrollTop;

      anime.remove('html, body');

      const animation = anime({
        targets: 'html, body',
        scrollTop: 0,
        duration: Math.max(200, distance),
        easing: 'easeInOutCubic',
      });

      return animation.finished;
    },
    changeAppColor() {
      if (this.currentNetwork.type === 'color') {
        this.appBGColor.backgroundColor = this.currentNetwork.color;
        this.appBGColor.backgroundImage = '';
      } else {
        this.appBGColor.backgroundImage = this.currentNetwork.color;
      }
    },
    changeNetwork() {
      if (this.showInteractionHint) this.showInteractionHint = false;
      if (window.scrollY !== 0) this.animateToTop().then(() => this.selectNetwork());
      else this.selectNetwork();
    },
    imgLoad() {
      this.$nextTick(() => {
        this.showReceipt = true;
        this.loading = false;

        anime({
          targets: 'html, body',
          scrollTop: document.body.offsetHeight - window.innerHeight,
          duration: this.currentNetwork.height * 2,
          delay: 1500,
          easing: 'easeInOutCubic',
          complete: () => {
            if (!this.interactionHintShown) {
              this.showInteractionHint = true;
              this.interactionHintShown = true;
            }
          },
        });
      });
    },
    selectNetwork() {
      if (!this.networks) this.networks = Object.keys(this.socialNetworks);

      anime.remove('html, body, .receipt-container');
      if (this.$el) this.$el.querySelector('.receipt-container').style = ''; // HACK: to remove the styles leftover by animejs

      this.showReceipt = false;
      this.loading = true;

      const index = Math.floor(Math.random() * this.networks.length);

      if (this.shownNetworks.indexOf(index) < 0) {
        this.currentNetwork = this.socialNetworks[this.networks[index]];
        this.currentNetworkIndex = index;
        this.shownNetworks.push(index);
      } else if (this.shownNetworks.length < this.networks.length) {
        this.selectNetwork();
      } else if (index !== this.currentNetworkIndex) {
        this.currentNetwork = this.socialNetworks[this.networks[index]];
        this.currentNetworkIndex = index;
      } else {
        this.selectNetwork();
      }
    },
    swipe(e) {
      const currentX = e.type === 'touchmove' ? e.changedTouches[0].clientX : e.clientX;
      const distance = currentX - this.swipeData.start;

      if (!this.swipeData.ticking) {
        window.requestAnimationFrame(() => {
          if (distance > 20) this.swipeAnimation.seek((distance - 20) / this.swipeData.maxDistance * 1000);
          this.swipeData.ticking = false;
        });

        this.swipeData.ticking = true;
      }
    },
    swipeEnd(e) {
      if (e.type === 'touchend') {
        window.removeEventListener('touchmove', this.swipe);
        window.removeEventListener('touchend', this.swipeEnd);
      } else {
        window.removeEventListener('mousemove', this.swipe);
        window.removeEventListener('mouseup', this.swipeEnd);
      }

      if (this.swipeAnimation.currentTime > 200) {
        if (this.swipeAnimation.reversed) this.swipeAnimation.reverse();
        this.swipeAnimation.play();
        this.swipeAnimation.finished.then(() => this.changeNetwork());
      } else if (this.swipeAnimation.currentTime !== 0) {
        if (!this.swipeAnimation.reversed) this.swipeAnimation.reverse();
        this.swipeAnimation.play();
      }
    },
    swipeStart(e) {
      if (e.type === 'touchstart') {
        window.addEventListener('touchmove', this.swipe);
        window.addEventListener('touchend', this.swipeEnd);
        this.swipeData.start = e.changedTouches[0].clientX;
        this.swipeData.maxDistance = window.innerWidth - this.swipeData.start;
      } else {
        window.addEventListener('mousemove', this.swipe);
        window.addEventListener('mouseup', this.swipeEnd);
        this.swipeData.start = e.clientX;
        this.swipeData.maxDistance = window.innerWidth - this.swipeData.start;
      }
    },
  },
};
</script>

<style lang="stylus">
@import './assets/fonts.css'

html,
body
  margin: 0
  padding: 0
  font-size: 16px

#app
  font-family: 'PT Sans', sans-serif
  -webkit-font-smoothing: antialiased
  -moz-osx-font-smoothing: grayscale
  min-height: 100vh
  background-size: cover
  overflow: hidden
  box-sizing: border-box
  color: rgba(255, 255, 255, 0.5);
  position: relative
  transition: color 200ms ease
  hyphens: auto

  #ink
    // border-radius: 50%
    // width: 200vmax
    // height: 200vmax
    // position: absolute
    // top: calc(50vh - 200vmax / 2)
    // left: calc(50vw - 200vmax / 2)
    position: absolute
    top: 0
    width: 100%
    height: 100%
    transform-origin: top

    &.ink-enter-active
      transition: transform 1.2s ease

    &.ink-enter
      transform: scaleY(0)

  .foreground
    z-index: 1
    position: relative
    min-height: calc(100vh - 128px)
    padding-bottom: 128px
    box-sizing: border-box

    .loading-text
      margin: 16px auto 0 auto
      font-family: 'Montserrat', sans-serif
      font-weight: 900
      text-transform: uppercase
      font-size: 1rem
      width: 95%
      max-width: 550px
      text-align: center
      animation: pulse 1s ease alternate infinite

      @keyframes pulse
        from
          opacity: 0
        to
          opacity: 1

    .slit
      width: 95%
      max-width: 550px
      background-color: rgba(0,0,0,0.4)
      height: 16px
      border-radius: 8px
      margin: 128px auto 0 auto

    .receipt-container
      overflow: hidden
      text-align: center
      width: 100%
      max-width: 500px
      margin: 0 auto
      margin-top: -8px
      padding: 30px
      padding-top: 0
      box-sizing: border-box
      transform-origin: top right
      touch-action: pan-y
      // transition: height 1s ease-out

      img
        border-radius: 4px
        width: 100%
        box-shadow: 0 0 8px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.2)

        &.slide-down-enter-active
          transition: transform 1s ease-out
          transition-delay: 500ms

        &.slide-down-enter
          transform: translateY(-100%)

    .main-footer
      position: absolute
      bottom: 0
      box-sizing: border-box
      padding: 32px
      width: 100vw
      display: flex
      align-items: flex-end
      justify-content: space-between

      @media only screen and (max-width: 1084px)
        flex-direction: column
        justify-content: center
        align-items: center
        text-align: center
        padding: 16px

      *
        flex-shrink: 0

      .title
        font-family: 'Montserrat', sans-serif
        font-weight: 900
        font-size: 2rem
        text-transform: uppercase
        line-height: 0.9em
        margin: 0
        width: 380px

        @media only screen and (max-width: 1084px)
          margin-bottom: 24px
          text-align: left
          width: auto

      .copyright-info
        text-align: center

        @media only screen and (max-width: 1084px)
          margin-bottom: 8px

      .navigation
        color: white
        width: 380px
        text-align: right

        @media only screen and (max-width: 1084px)
          text-align: center

        ul
          list-style: none
          margin: 0
          padding: 0

          li
            display: inline-block
            cursor: pointer
            opacity: 0.5
            transition: opacity 200ms ease
            font-family: 'Montserrat', sans-serif
            font-weight: 900

            &:not(:last-child)
              margin-right: 24px

            &:hover
              opacity: 1
</style>
